import { GithubActionsClient } from '@backstage-community/plugin-github-actions';
import { ConfigApi } from '@backstage/core-plugin-api';
import { ScmAuthApi } from '@backstage/integration-react';
import { readGithubIntegrationConfigs } from '@backstage/integration';
import { Octokit } from '@octokit/rest';
import { GithubActionsHealthApi } from './GitHubActionsHealthApi';
import { Workflow, WorkflowRun, WorkflowRunStatus } from './types';

export class GitHubActionsHealthClient implements GithubActionsHealthApi {
	private readonly client: GithubActionsClient;
	private readonly configApi: ConfigApi;
	private readonly scmAuthApi: ScmAuthApi;

	constructor(configApi: ConfigApi, scmAuthApi: ScmAuthApi) {
		this.client = new GithubActionsClient({ configApi, scmAuthApi });
		this.configApi = configApi;
		this.scmAuthApi = scmAuthApi;
	}

	private async getOctokit(hostname: string = 'github.com'): Promise<Octokit> {
		const { token } = await this.scmAuthApi.getCredentials({
			url: `https://${hostname}/`,
			additionalScope: {
				customScopes: {
					github: ['repo'],
				},
			},
		});
		const configs = readGithubIntegrationConfigs(
			this.configApi.getOptionalConfigArray('integrations.github') ?? [],
		);
		const githubIntegrationConfig = configs.find(v => v.host === hostname);
		const baseUrl = githubIntegrationConfig?.apiBaseUrl;
		return new Octokit({ auth: token, baseUrl });
	}

	async listWorkflowsForRepository(options: {
		hostname?: string;
		owner: string;
		repo: string;
	}): Promise<Workflow[]> {
		const client = await this.getOctokit();
		const response = await client.actions.listRepoWorkflows({
			owner: options.owner,
			repo: options.repo,
		});
		return response.data.workflows.map(w => ({ id: w.id, name: w.name }));
	}

	async listWorkflowRunsForWorkflowId(options: {
		hostname?: string;
		owner: string;
		repo: string;
		workflowId: number;
	}): Promise<WorkflowRun[]> {
		const client = await this.getOctokit();
		const response = await client.actions.listWorkflowRuns({
			owner: options.owner,
			repo: options.repo,
			workflow_id: options.workflowId,
		});

		return response.data.workflow_runs.map(run => {
			const startTime = new Date(run.created_at);
			const endTime = new Date(run.updated_at);
			const durationInSeconds = Math.floor(
				(endTime.getTime() - startTime.getTime()) / 1000,
			);

			const status: WorkflowRunStatus =
				(run.status as WorkflowRunStatus) ?? 'running';

			const retries = (run.run_attempt ?? 1) - 1;

			return {
				id: run.id,
				duration: durationInSeconds,
				status,
				retries,
			};
		});
	}

	async listWorkflowRunsForRepository(options: {
		hostname?: string;
		owner: string;
		repo: string;
	}): Promise<WorkflowRun[]> {
		const client = await this.getOctokit();
		const response = await client.actions.listWorkflowRunsForRepo({
			owner: options.owner,
			repo: options.repo,
		});

		return response.data.workflow_runs.map(run => {
			const startTime = new Date(run.created_at);
			const endTime = new Date(run.updated_at);
			const durationInSeconds = Math.floor(
				(endTime.getTime() - startTime.getTime()) / 1000,
			);

			const status: WorkflowRunStatus =
				(run.status as WorkflowRunStatus) ?? 'running';

			const retries = (run.run_attempt ?? 1) - 1;

			return {
				id: run.id,
				duration: durationInSeconds,
				status,
				retries,
			};
		});
	}

	async listWorkflowRuns(options: {
		hostname?: string;
		owner: string;
		repo: string;
		pageSize?: number;
		page?: number;
		branch?: string;
	}) {
		return this.client.listWorkflowRuns(options);
	}

	async getWorkflow(options: {
		hostname?: string;
		owner: string;
		repo: string;
		id: number;
	}) {
		return this.client.getWorkflow(options);
	}

	async getWorkflowRun(options: {
		hostname?: string;
		owner: string;
		repo: string;
		id: number;
	}) {
		return this.client.getWorkflowRun(options);
	}

	async reRunWorkflow(options: {
		hostname?: string;
		owner: string;
		repo: string;
		runId: number;
	}) {
		return this.client.reRunWorkflow(options);
	}

	async listJobsForWorkflowRun(options: {
		hostname?: string;
		owner: string;
		repo: string;
		id: number;
		pageSize?: number;
		page?: number;
	}) {
		return this.client.listJobsForWorkflowRun(options);
	}

	async downloadJobLogsForWorkflowRun(options: {
		hostname?: string;
		owner: string;
		repo: string;
		runId: number;
	}) {
		return this.client.downloadJobLogsForWorkflowRun(options);
	}

	async listBranches(options: {
		hostname?: string;
		owner: string;
		repo: string;
		page?: number;
	}) {
		return this.client.listBranches(options);
	}

	async getDefaultBranch(options: {
		hostname?: string;
		owner: string;
		repo: string;
	}) {
		return this.client.getDefaultBranch(options);
	}
}
